// hooks
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import useAlerts from '@/hooks/useAlerts';

// components
import IconButton from '@mui/material/IconButton';
import Tooltip from '@/components/shared/Tooltip';

// icons
import CreateIcon from '@mui/icons-material/BorderColor';

// utils
import devLog from '@/utils/devLog';

// services
import { postNewConversation } from '@/features/messenger/services/conversations.services';
import { postNewMessage } from '@/features/messenger/services/messages.services';

// CreateNewConversation button
// is inside ConversationCreateModal
export default function CreateNewConversationButton({
  currentUser,
  receiverUser,
  newMessageText,
}) {
  const dispatch = useDispatch();
  const { setCurrentAlert } = useAlerts();

  const handleCreateConversation = useCallback(async () => {
    try {
      if (!newMessageText) {
        setCurrentAlert({
          type: 'Error',
          message: 'cannot start conversation, no message provided!',
        });
        return;
      }

      // create the conversation
      const newConversation = await postNewConversation({
        senderId: currentUser._id,
        receiverId: receiverUser._id,
      });

      devLog('new conversation created! ', newConversation);
      // add the initial messsage;

      const newlyCreatedMessage = await postNewMessage({
        conversationId: newConversation._id,
        text: newMessageText,
        receiverId: receiverUser._id,
      });

      devLog('new message added to the conversation!', newlyCreatedMessage);

      dispatch({
        type: 'messenger/addNewConversation',
        payload: newConversation,
      });

      dispatch({ type: 'general/conversationCreateModalClose' });

      setTimeout(() => {
        dispatch({
          type: 'general/chatRoomOpen',
          payload: { conversation: newConversation, isOpen: true },
        });
      }, 1);

      setCurrentAlert({
        type: 'Success',
        message: `started a new conversation with ${receiverUser.name}`,
      });
      return;
    } catch (error) {
      console.error(error);
      setCurrentAlert({
        type: 'Error',
        message: `Error creating new conversation with ${receiverUser.name}`,
      });
    }
  }, [
    currentUser._id,
    receiverUser._id,
    receiverUser.name,
    dispatch,
    setCurrentAlert,
    newMessageText,
  ]);

  useEffect(() => {
    const handleUserKeyPress = (e) => {
      let shiftKeyPressed = e.shiftKey;
      if (e.key !== 'Enter') return;
      if (!e.target.value) {
        e.preventDefault();
        return;
      }
      // if key is enter and shift isn't pressed submit, else enter a new line
      if (!shiftKeyPressed) {
        handleCreateConversation();
        return;
      }
    };

    window.addEventListener('keydown', handleUserKeyPress);
    return () => {
      window.removeEventListener('keydown', handleUserKeyPress);
    };
  }, [handleCreateConversation]);

  if (!currentUser._id) return null;

  return (
    <Tooltip title="Start the conversation">
      <IconButton onClick={handleCreateConversation}>
        <CreateIcon />
      </IconButton>
    </Tooltip>
  );
}
